/**
 * `<LockedGuardrailsPanel>` — single-sourced, novel-form-class component
 * (B-22b, HoD §1b / §2b).
 *
 * Used on both:
 *   * Surface 1 (Settings → AI): read-only, all roles, shows current locked text.
 *   * Surface 2 (SA /admin/bias-guardrails): read-only, reused as-is.
 *
 * HoD §6 acceptance: this component MUST be single-sourced; engineer grep
 * confirms no copy on Surface 2 — both surfaces import from this module.
 *
 * Visual:
 *   - Flat-recessed, bordered (NOT raised Card).
 *   - Mono font, text-[13px], leading-[1.65], white-space: pre-wrap.
 *   - Top-right chip: `v{N} · locked {date}` + copy icon.
 *   - Max-height 480 with overflow scroll.
 *
 * IV-24 frontend half: rejection_reason rendered via React text node ONLY;
 * NEVER `dangerouslySetInnerHTML`. This component renders `text` directly
 * via React's auto-escaping JSX text node.
 */
import type { JSX } from "react";
import { CopyButton } from "@/components/common/CopyButton";

interface LockedGuardrailsPanelProps {
  /** Locked Layer 2 text. Rendered verbatim in mono pre-wrap. */
  text: string;
  /** Active version number. */
  version: number;
  /** ISO date string (e.g. "2026-06-05") shown in the top-right chip. */
  effectiveAt: string | null;
  /**
   * B-22c additive prop (HoD §1a): when ``true``, the top-right
   * version+locked-date chip is suppressed (the panel renders only the
   * mono pre-wrap text + copy icon). Calibration cards use this because
   * the CalibrationCard's own header already carries the chip; we
   * don't want a second chip rendered inside the panel.
   *
   * Default ``false`` — B-22b consumers (BiasGuardrailsSection) are
   * untouched (HoD acceptance gate #3).
   */
  chipHidden?: boolean;
}

function formatLockedDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso.slice(0, 10);
  }
}

export function LockedGuardrailsPanel({
  text,
  version,
  effectiveAt,
  chipHidden = false,
}: LockedGuardrailsPanelProps): JSX.Element {
  const dateLabel = formatLockedDate(effectiveAt);

  return (
    <div
      data-testid="locked-guardrails-panel"
      className="relative rounded-xl border border-border-default bg-surface-inset p-5"
    >
      {/* Top-right chip: v{N} · locked {date} + copy icon.
        * B-22c HoD §1a: ``chipHidden`` suppresses the version chip when the
        * parent (CalibrationCard) renders its own authoritative chip. The
        * copy icon stays in all cases. */}
      <div className="absolute right-3 top-3 flex items-center gap-2">
        {!chipHidden ? (
          <span
            className="rounded-md border border-border-default bg-surface-elevated px-2 py-0.5 text-[11px] font-medium text-text-secondary dark:bg-white/[0.04]"
            data-testid="guardrails-version-chip"
          >
            v{version} · locked {dateLabel}
          </span>
        ) : null}
        <CopyButton
          value={text}
          toastLabel="locked guardrails text"
          ariaLabel="Copy locked guardrails text"
        />
      </div>
      {/* Mono pre-wrap text canvas — max height 480 scrollable */}
      <pre
        className="mt-6 max-h-[480px] overflow-y-auto whitespace-pre-wrap break-words font-mono text-[13px] leading-[1.65] text-text-primary dark:text-text-primary"
        data-testid="locked-guardrails-text"
      >
        {text}
      </pre>
    </div>
  );
}
